import PropTypes from 'prop-types';
import React, { useRef } from 'react';
import { fromNow } from '../../../utilities/fromNowWrapper.js';
import { capitalise } from 'utilities/capitaliseString.js';
import './Profile.scss';
import {
  GeckoCardWrapper,
  GeckoCardHeader,
  GeckoCardBody,
  GeckoCardFooter,
  GeckoAvatar,
  GeckoLabel,
  GeckoBtnsSorted,
  GeckoModalService,
  GeckoModal,
  GECKO_CONSTS,
} from 'ui';
import CONSTS from 'constants';

const badgeClasses = {
  Available: 'success',
  Offline: 'default',
  Unavailable: 'warning',
  Unknown: 'info',
};

const ConfirmDeleteModal = ({ deleteAvatar }) => {
  const confirmDeleteBtns = [
    {
      name: 'Cancel',
      handler: () => new GeckoModalService().close(),
    },
    {
      name: 'Remove',
      handler: () => {
        new GeckoModalService().close();
        deleteAvatar();
      },
      type: GECKO_CONSTS.BTN_TYPE_DANGER,
    },
  ];

  return (
    <GeckoModal name={'Delete Avatar Image'} btns={confirmDeleteBtns}>
      Are you sure you wish to remove the image?
    </GeckoModal>
  );
};

const Profile = ({
  toggleAgentEditing,
  title,
  agent,
  groups,
  uploadAvatar,
  deleteAvatar,
  children,
}) => {
  const attachmentFile = useRef();

  const footerBtns = [
    {
      name: 'Edit Agent Details',
      type: 'default',
      handler: () => toggleAgentEditing(),
    },
  ];

  const openUploadAttachmentDialog = () => {
    attachmentFile.current.click();
  };

  const openDeleteAvatarConfirmModal = () => {
    new GeckoModalService().open(() => (
      <ConfirmDeleteModal deleteAvatar={deleteAvatar} />
    ));
  };

  const renderLastSeenTime = (agent) => {
    if (agent.presence === 'Online') {
      return 'now';
    }
    return agent.last_active ? fromNow(Date.parse(agent.last_active)) : 'never';
  };

  const getAgentCrossPlatformStatus = (agent) => {
    if (agent.presence === 'Online') {
      return agent.web_available || agent.mobile_available
        ? 'Available'
        : 'Unavailable';
    } else {
      return 'Offline';
    }
  };

  const getOnClickAction = (hasProfileImage) =>
    hasProfileImage ? openDeleteAvatarConfirmModal : openUploadAttachmentDialog;

  const getIconName = (hasProfileImage) =>
    hasProfileImage ? 'fa-trash-alt' : 'fa-camera';

  const renderHeader = () => {
    return <GeckoCardHeader name={title} />;
  };

  const renderBody = () => {
    const hasProfileImage = !!agent.additional.profile_picture_url;
    const agentType = groups.find((group) => group.id === agent.group_id).type;
    return (
      <div className="AgentProfile">
        <div className="dp_container">
          <div className="text-center">
            <GeckoAvatar
              name={`${agent.full_name}`}
              first_name={agent.first_name}
              last_name={agent.last_name}
              size="lg"
              color={agent.additional.avatar_colour || ''}
              bgImage={agent.additional.profile_picture_url || ''}
            />
            <div
              className="avatar-change-dp"
              onClick={getOnClickAction(hasProfileImage)}
              role="button"
              aria-label="avatar-change"
            >
              {!hasProfileImage && (
                <input
                  name="file"
                  ref={attachmentFile}
                  onChange={uploadAvatar}
                  style={{ display: 'none' }}
                  accept={CONSTS.IMAGE_ACCEPT_MIME_TYPES}
                  type="file"
                />
              )}
              <i className={`far ${getIconName(hasProfileImage)}`} />
            </div>
          </div>
        </div>
        <div className="profile-info">
          <h1 id="name">
            {`${agent.full_name}`}
            &nbsp;
            {`(${capitalise(agentType)})`}
          </h1>
          <div className="profile-meta">ID: {agent.uuid}</div>
          <div id="email" className="profile-meta">
            <a href={`mailto:${agent.email}`}>{agent.email}</a>
          </div>
          <div id="tel" className="profile-meta">
            {agent.tel}
          </div>
          <div className="profile-availability">
            <GeckoLabel
              name={getAgentCrossPlatformStatus(agent)}
              type={badgeClasses[getAgentCrossPlatformStatus(agent)]}
            />
            <span className="profile-availability-last-seen">
              Last seen: {renderLastSeenTime(agent)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderFooter = () => {
    return (
      <GeckoCardFooter>
        <GeckoBtnsSorted items={footerBtns} />
      </GeckoCardFooter>
    );
  };

  return (
    <GeckoCardWrapper>
      {renderHeader()}
      <GeckoCardBody padded>
        {renderBody()}
        <div>{children}</div>
      </GeckoCardBody>
      {renderFooter()}
    </GeckoCardWrapper>
  );
};

Profile.defaultProps = {
  title: '',
};

Profile.propTypes = {
  toggleAgentEditing: PropTypes.func.isRequired,
  title: PropTypes.string,
  groups: PropTypes.array.isRequired,
  agent: PropTypes.object.isRequired,
};

export default Profile;
